import { Route, Router } from "@solidjs/router";
import { lazy, onMount, onCleanup, Suspense } from "solid-js";
import type { JSX } from "solid-js";
import { initializeAirbrxAgent, matchAirbrxRoute } from "@airbrx/ui-components";
import { AuthWrapper } from "./components/AuthWrapper";
import { DashboardLayout } from "./components/DashboardLayout";
import { PageErrorBoundary } from "./components/PageErrorBoundary";
import { PageSkeleton } from "./components/PageSkeleton";
import { BrandingProvider } from "./components/BrandingProvider";
import { ToastProvider } from "./components/ui";
import { CP_AGENT_ROUTES } from "./lib/agentRoutes";
import { analytics } from "./lib/analytics";
import { trace } from "./lib/trace";
import { initializeCsrfToken } from "./utils/csrf";

function setDocumentTitleForPath(pathname: string): void {
  const match = matchAirbrxRoute(pathname, CP_AGENT_ROUTES);
  document.title = match ? `${match.title} - AirBrx Deploy` : "AirBrx Deploy";
}

const AgentRuntimeObserver = () => {
  let agent = typeof window !== "undefined"
    ? initializeAirbrxAgent({ app: "control-plane", routes: CP_AGENT_ROUTES })
    : undefined;

  let lastPathname = "";
  let restorePushState: (() => void) | undefined;
  let restoreReplaceState: (() => void) | undefined;

  const syncRoute = () => {
    if (typeof window === "undefined") return;
    const pathname = window.location.pathname;
    if (pathname === lastPathname) return;
    lastPathname = pathname;
    const route = matchAirbrxRoute(pathname, CP_AGENT_ROUTES);
    setDocumentTitleForPath(pathname);
    agent?.updateRoute(pathname);
    trace.navigation("Route changed", {
      path: pathname,
      routeId: route?.id ?? "unmapped",
      routeTitle: route?.title ?? "unknown",
    }, "debug");
  };

  onMount(() => {
    if (typeof window === "undefined") return;
    syncRoute();

    const handleNavigation = () => syncRoute();
    const originalPushState = window.history.pushState.bind(window.history);
    const originalReplaceState = window.history.replaceState.bind(window.history);

    window.history.pushState = ((...args: Parameters<History["pushState"]>) => {
      originalPushState(...args);
      syncRoute();
    }) as History["pushState"];
    window.history.replaceState = ((...args: Parameters<History["replaceState"]>) => {
      originalReplaceState(...args);
      syncRoute();
    }) as History["replaceState"];

    restorePushState = () => {
      window.history.pushState = originalPushState;
    };
    restoreReplaceState = () => {
      window.history.replaceState = originalReplaceState;
    };

    window.addEventListener("popstate", handleNavigation);
    window.addEventListener("hashchange", handleNavigation);

    onCleanup(() => {
      window.removeEventListener("popstate", handleNavigation);
      window.removeEventListener("hashchange", handleNavigation);
      restorePushState?.();
      restoreReplaceState?.();
      agent?.destroy();
    });
  });
  return null;
};

// ---------------------------------------------------------------------------
// Lazy-loaded page components
// Each page file uses a named export, so we re-map to { default }.
// ---------------------------------------------------------------------------
const AccountApiKeys = lazy(() => import("./pages/AccountApiKeys").then(m => ({ default: m.AccountApiKeys })));
const BrandingSettings = lazy(() => import("./pages/BrandingSettings").then(m => ({ default: m.BrandingSettings })));
const AdminDashboard = lazy(() => import("./pages/AdminDashboard").then(m => ({ default: m.AdminDashboard })));
const BillingOverview = lazy(() => import("./pages/BillingOverview").then(m => ({ default: m.BillingOverview })));
const BillingHistory = lazy(() => import("./pages/BillingHistory").then(m => ({ default: m.BillingHistory })));
const PlanSelector = lazy(() => import("./pages/PlanSelector").then(m => ({ default: m.PlanSelector })));
const UsageDashboard = lazy(() => import("./pages/UsageDashboard").then(m => ({ default: m.UsageDashboard })));
const Signup = lazy(() => import("./pages/Signup").then(m => ({ default: m.Signup })));
const StatusPage = lazy(() => import("./pages/StatusPage").then(m => ({ default: m.StatusPage })));
const Analytics = lazy(() => import("./pages/Analytics").then(m => ({ default: m.Analytics })));
const AccountAudit = lazy(() => import("./pages/AccountAudit").then(m => ({ default: m.AccountAudit })));
const AccountGateways = lazy(() => import("./pages/AccountGateways").then(m => ({ default: m.AccountGateways })));
const AccountLogs = lazy(() => import("./pages/AccountLogs").then(m => ({ default: m.AccountLogs })));
const AccountSettings = lazy(() => import("./pages/AccountSettings").then(m => ({ default: m.AccountSettings })));
const MfaSetup = lazy(() => import("./pages/MfaSetup").then(m => ({ default: m.MfaSetup })));
const OidcSettings = lazy(() => import("./pages/OidcSettings").then(m => ({ default: m.OidcSettings })));
const AuditLogs = lazy(() => import("./pages/AuditLogs").then(m => ({ default: m.AuditLogs })));
const Azurite = lazy(() => import("./pages/Azurite").then(m => ({ default: m.Azurite })));
const BundleDetails = lazy(() => import("./pages/BundleDetails").then(m => ({ default: m.BundleDetails })));
const CacheOverview = lazy(() => import("./pages/CacheOverview").then(m => ({ default: m.CacheOverview })));
const CacheRules = lazy(() => import("./pages/CacheRules").then(m => ({ default: m.CacheRules })));
const BundleFactory = lazy(() => import("./pages/BundleFactory").then(m => ({ default: m.BundleFactory })));
const BundleList = lazy(() => import("./pages/BundleList").then(m => ({ default: m.BundleList })));
const ClientCloudCredentialCreate = lazy(() => import("./pages/ClientCloudCredentialCreate").then(m => ({ default: m.ClientCloudCredentialCreate })));
const ClientCloudCredentialDetails = lazy(() => import("./pages/ClientCloudCredentialDetails").then(m => ({ default: m.ClientCloudCredentialDetails })));
const ClientCloudCredentials = lazy(() => import("./pages/ClientCloudCredentials").then(m => ({ default: m.ClientCloudCredentials })));
const CloudProvisioning = lazy(() => import("./pages/CloudProvisioning").then(m => ({ default: m.CloudProvisioning })));
const ClientCreate = lazy(() => import("./pages/ClientCreate").then(m => ({ default: m.ClientCreate })));
const ClientDeploymentDetails = lazy(() => import("./pages/ClientDeploymentDetails").then(m => ({ default: m.ClientDeploymentDetails })));
const ClientDeployments = lazy(() => import("./pages/ClientDeployments").then(m => ({ default: m.ClientDeployments })));
const ClientDetails = lazy(() => import("./pages/ClientDetails").then(m => ({ default: m.ClientDetails })));
const Clients = lazy(() => import("./pages/Clients").then(m => ({ default: m.Clients })));
const ClientUsers = lazy(() => import("./pages/ClientUsers").then(m => ({ default: m.ClientUsers })));
const CloudResources = lazy(() => import("./pages/CloudResources").then(m => ({ default: m.CloudResources })));
const ComponentVersions = lazy(() => import("./pages/ComponentVersions").then(m => ({ default: m.ComponentVersions })));
const CreateCredential = lazy(() => import("./pages/CreateCredential").then(m => ({ default: m.CreateCredential })));
const CredentialDetails = lazy(() => import("./pages/CredentialDetails").then(m => ({ default: m.CredentialDetails })));
const Credentials = lazy(() => import("./pages/Credentials").then(m => ({ default: m.Credentials })));
const CredentialsGlobal = lazy(() => import("./pages/CredentialsGlobal").then(m => ({ default: m.CredentialsGlobal })));
const Dashboard = lazy(() => import("./pages/Dashboard").then(m => ({ default: m.Dashboard })));
const DeploymentArchives = lazy(() => import("./pages/DeploymentArchives").then(m => ({ default: m.DeploymentArchives })));
const DeploymentDetails = lazy(() => import("./pages/DeploymentDetails").then(m => ({ default: m.DeploymentDetails })));
const DeploymentLogs = lazy(() => import("./pages/DeploymentLogs").then(m => ({ default: m.DeploymentLogs })));
const DevContainer = lazy(() => import("./pages/DevContainer").then(m => ({ default: m.DevContainer })));
const DeviceAuth = lazy(() => import("./pages/DeviceAuth").then(m => ({ default: m.DeviceAuth })));
const DiagnosticAnalysis = lazy(() => import("./pages/DiagnosticAnalysis").then(m => ({ default: m.DiagnosticAnalysis })));
const DiagnosticList = lazy(() => import("./pages/DiagnosticList").then(m => ({ default: m.DiagnosticList })));
const DiagnosticViewer = lazy(() => import("./pages/DiagnosticViewer").then(m => ({ default: m.DiagnosticViewer })));
const FleetBulkOperations = lazy(() => import("./pages/FleetBulkOperations").then(m => ({ default: m.FleetBulkOperations })));
const FleetInstanceDetails = lazy(() => import("./pages/FleetInstanceDetails").then(m => ({ default: m.FleetInstanceDetails })));
const FleetOverview = lazy(() => import("./pages/FleetOverview").then(m => ({ default: m.FleetOverview })));
const GatewayConfigHistory = lazy(() => import("./pages/GatewayConfigHistory").then(m => ({ default: m.GatewayConfigHistory })));
const GatewayCreate = lazy(() => import("./pages/GatewayCreate").then(m => ({ default: m.GatewayCreate })));
const GatewayDetails = lazy(() => import("./pages/GatewayDetails").then(m => ({ default: m.GatewayDetails })));
const Gateways = lazy(() => import("./pages/Gateways").then(m => ({ default: m.Gateways })));
const HealthDashboard = lazy(() => import("./pages/HealthDashboard").then(m => ({ default: m.HealthDashboard })));
const Home = lazy(() => import("./pages/Home").then(m => ({ default: m.Home })));
const Website = lazy(() => import("./pages/Website").then(m => ({ default: m.Website })));
const InstanceCRM = lazy(() => import("./pages/InstanceCRM").then(m => ({ default: m.InstanceCRM })));
const InstanceDetail = lazy(() => import("./pages/InstanceDetail").then(m => ({ default: m.InstanceDetail })));
const InstanceList = lazy(() => import("./pages/InstanceList").then(m => ({ default: m.InstanceList })));
const LiveDeployment = lazy(() => import("./pages/LiveDeployment").then(m => ({ default: m.LiveDeployment })));
const LocalStack = lazy(() => import("./pages/LocalStack").then(m => ({ default: m.LocalStack })));
const ManagedInstances = lazy(() => import("./pages/ManagedInstances").then(m => ({ default: m.ManagedInstances })));
const NewDeployment = lazy(() => import("./pages/NewDeployment").then(m => ({ default: m.NewDeployment })));
const NotFound = lazy(() => import("./pages/NotFound").then(m => ({ default: m.NotFound })));
const NotificationInbox = lazy(() => import("./pages/NotificationInbox").then(m => ({ default: m.NotificationInbox })));
const OAuthCallback = lazy(() => import("./pages/OAuthCallback").then(m => ({ default: m.OAuthCallback })));
const Observability = lazy(() => import("./pages/Observability").then(m => ({ default: m.Observability })));
const Onboarding = lazy(() => import("./pages/Onboarding").then(m => ({ default: m.Onboarding })));
const GettingStarted = lazy(() => import("./pages/GettingStarted").then(m => ({ default: m.GettingStarted })));
const ProviderSecrets = lazy(() => import("./pages/ProviderSecrets").then(m => ({ default: m.ProviderSecrets })));
const ProvisioningDetail = lazy(() => import("./pages/ProvisioningDetail").then(m => ({ default: m.ProvisioningDetail })));
const ProvisioningList = lazy(() => import("./pages/ProvisioningList").then(m => ({ default: m.ProvisioningList })));
const ProvisioningNew = lazy(() => import("./pages/ProvisioningNew").then(m => ({ default: m.ProvisioningNew })));
const ReconciliationReports = lazy(() => import("./pages/ReconciliationReports").then(m => ({ default: m.ReconciliationReports })));
const RoleCreate = lazy(() => import("./pages/RoleCreate").then(m => ({ default: m.RoleCreate })));
const RoleDetails = lazy(() => import("./pages/RoleDetails").then(m => ({ default: m.RoleDetails })));
const RoleEdit = lazy(() => import("./pages/RoleEdit").then(m => ({ default: m.RoleEdit })));
const Roles = lazy(() => import("./pages/Roles").then(m => ({ default: m.Roles })));
const Sessions = lazy(() => import("./pages/Sessions").then(m => ({ default: m.Sessions })));
const Settings = lazy(() => import("./pages/Settings").then(m => ({ default: m.Settings })));
const ShardStats = lazy(() => import("./pages/ShardStats").then(m => ({ default: m.ShardStats })));
const StackOverview = lazy(() => import("./pages/StackOverview").then(m => ({ default: m.StackOverview })));
const StackSecrets = lazy(() => import("./pages/StackSecrets").then(m => ({ default: m.StackSecrets })));
const Tunnels = lazy(() => import("./pages/Tunnels").then(m => ({ default: m.Tunnels })));
const UpdateCampaignDetail = lazy(() => import("./pages/UpdateCampaignDetail").then(m => ({ default: m.UpdateCampaignDetail })));
const UpdateCampaignList = lazy(() => import("./pages/UpdateCampaignList").then(m => ({ default: m.UpdateCampaignList })));
const UpdateCampaignNew = lazy(() => import("./pages/UpdateCampaignNew").then(m => ({ default: m.UpdateCampaignNew })));
const ComplianceReports = lazy(() => import("./pages/ComplianceReports").then(m => ({ default: m.ComplianceReports })));
const UserManagement = lazy(() => import("./pages/UserManagement").then(m => ({ default: m.UserManagement })));
const WebhookManagement = lazy(() => import("./pages/WebhookManagement").then(m => ({ default: m.WebhookManagement })));

const ProtectedPage = (props: { children: JSX.Element }) => (
  <AuthWrapper>
    <DashboardLayout>
      <PageErrorBoundary>
        {props.children}
      </PageErrorBoundary>
    </DashboardLayout>
  </AuthWrapper>
);

const App = () => {
  // Initialize CSRF protection on app mount
  onMount(async () => {
    trace.lifecycle("App mounted", {
      url: window.location.href,
      path: window.location.pathname,
      userAgent: navigator.userAgent.slice(0, 100),
    });
    try {
      await initializeCsrfToken();
      trace.lifecycle("CSRF token initialized");
    } catch (error: unknown) {
      trace.lifecycle("CSRF init failed", { error: String(error) }, "error");
      analytics.error("csrf_init_error", error, { page: "App" });
    }
  });
  return (
    <BrandingProvider>
    <ToastProvider position="top-right">
      <Router>
        <AgentRuntimeObserver />
        <Suspense fallback={<PageSkeleton />}>
        {/* Public routes */}
        <Route path='/' component={() => <PageErrorBoundary><Website /></PageErrorBoundary>} />
      <Route path='/home' component={() => <PageErrorBoundary><Home /></PageErrorBoundary>} />
      <Route path='/auth/callback' component={() => <PageErrorBoundary><OAuthCallback /></PageErrorBoundary>} />
      <Route path='/device' component={() => <PageErrorBoundary><DeviceAuth /></PageErrorBoundary>} />
      <Route path='/signup' component={() => <PageErrorBoundary><Signup /></PageErrorBoundary>} />
      <Route path='/status' component={() => <PageErrorBoundary><StatusPage /></PageErrorBoundary>} />

      {/* Onboarding wizard (first-time users) */}
      <Route path='/onboarding' component={() => (
        <AuthWrapper>
          <PageErrorBoundary>
            <Onboarding />
          </PageErrorBoundary>
        </AuthWrapper>
      )} />

      {/* Getting started wizard (setup flow for new admins) */}
      <Route path='/getting-started' component={() => <ProtectedPage><GettingStarted /></ProtectedPage>} />

      {/* Protected routes with dashboard layout */}
      <Route path='/dashboard' component={() => <ProtectedPage><Dashboard /></ProtectedPage>} />

      {/* Accounts routes (alias for clients) */}
      <Route path='/accounts' component={() => <ProtectedPage><Clients /></ProtectedPage>} />

      {/* Clients routes */}
      <Route path='/clients' component={() => <ProtectedPage><Clients /></ProtectedPage>} />
      <Route path='/clients/new' component={() => <ProtectedPage><ClientCreate /></ProtectedPage>} />
      <Route path='/clients/:id' component={() => <ProtectedPage><ClientDetails /></ProtectedPage>} />
      <Route path='/clients/:clientId/gateways' component={() => <ProtectedPage><AccountGateways /></ProtectedPage>} />
      <Route path='/clients/:clientId/gateways/new' component={() => <ProtectedPage><GatewayCreate /></ProtectedPage>} />
      <Route path='/clients/:clientId/users' component={() => <ProtectedPage><ClientUsers /></ProtectedPage>} />
      <Route path='/clients/:clientId/api-keys' component={() => <ProtectedPage><AccountApiKeys /></ProtectedPage>} />
      <Route path='/clients/:clientId/audit' component={() => <ProtectedPage><AccountAudit /></ProtectedPage>} />
      <Route path='/clients/:clientId/logs' component={() => <ProtectedPage><AccountLogs /></ProtectedPage>} />
      <Route path='/clients/:clientId/settings' component={() => <ProtectedPage><AccountSettings /></ProtectedPage>} />
      <Route path='/clients/:clientId/settings/oidc' component={() => <ProtectedPage><OidcSettings /></ProtectedPage>} />
      <Route path='/clients/:clientId/settings/branding' component={() => <ProtectedPage><BrandingSettings /></ProtectedPage>} />
      <Route path='/clients/:clientId/webhooks' component={() => <ProtectedPage><WebhookManagement /></ProtectedPage>} />
      <Route path='/clients/:clientId/compliance' component={() => <ProtectedPage><ComplianceReports /></ProtectedPage>} />
      <Route path='/clients/:clientId/credentials' component={() => <ProtectedPage><Credentials /></ProtectedPage>} />
      <Route path='/clients/:clientId/credentials/new' component={() => <ProtectedPage><CreateCredential /></ProtectedPage>} />
      <Route path='/clients/:clientId/credentials/:credentialId' component={() => <ProtectedPage><CredentialDetails /></ProtectedPage>} />

      {/* Client Cloud Credentials routes (for Tier 3 managed on-prem) */}
      <Route path='/clients/:clientId/cloud-credentials' component={() => <ProtectedPage><ClientCloudCredentials /></ProtectedPage>} />
      <Route path='/clients/:clientId/cloud-credentials/new' component={() => <ProtectedPage><ClientCloudCredentialCreate /></ProtectedPage>} />
      <Route path='/clients/:clientId/cloud-credentials/:credentialId' component={() => <ProtectedPage><ClientCloudCredentialDetails /></ProtectedPage>} />

      {/* Cloud Provisioning (Azure/GCP infrastructure) */}
      <Route path='/clients/:clientId/cloud/provision' component={() => <ProtectedPage><CloudProvisioning /></ProtectedPage>} />

      {/* Managed Instances route (Tier 3 - Your Managed Instances) */}
      <Route path='/clients/:clientId/managed-instances' component={() => <ProtectedPage><ManagedInstances /></ProtectedPage>} />

      <Route path='/clients/:clientId/deployments' component={() => <ProtectedPage><ClientDeployments /></ProtectedPage>} />
      <Route path='/clients/:clientId/deployments/new' component={() => <ProtectedPage><NewDeployment /></ProtectedPage>} />
      <Route path='/clients/:clientId/deployments/:deploymentId' component={() => <ProtectedPage><ClientDeploymentDetails /></ProtectedPage>} />
      <Route path='/clients/:clientId/deployments/:deploymentId/live' component={() => <ProtectedPage><LiveDeployment /></ProtectedPage>} />
      <Route path='/clients/:clientId/deployments/:deploymentId/logs' component={() => <ProtectedPage><DeploymentLogs /></ProtectedPage>} />
      <Route path='/clients/:clientId/deployments/:deploymentId/secrets' component={() => <ProtectedPage><StackSecrets /></ProtectedPage>} />
      <Route path='/clients/:clientId/deployments/:deploymentId/config-history' component={() => <ProtectedPage><GatewayConfigHistory /></ProtectedPage>} />

      {/* Gateway routes */}
      <Route path='/gateways' component={() => <ProtectedPage><Gateways /></ProtectedPage>} />
      <Route path='/gateways/new' component={() => <ProtectedPage><GatewayCreate /></ProtectedPage>} />
      <Route path='/gateways/:id' component={() => <ProtectedPage><GatewayDetails /></ProtectedPage>} />
      <Route path='/clients/:clientId/gateways/:gatewayId/config/history' component={() => <ProtectedPage><GatewayConfigHistory /></ProtectedPage>} />

      {/* Deployments routes */}
      <Route path='/deployments' component={() => <ProtectedPage><DeploymentDetails /></ProtectedPage>} />
      <Route path='/deployments/:id' component={() => <ProtectedPage><DeploymentDetails /></ProtectedPage>} />

      {/* Fleet Management routes */}
      <Route path='/fleet' component={() => <ProtectedPage><FleetOverview /></ProtectedPage>} />
      <Route path='/fleet/instances' component={() => <ProtectedPage><InstanceList /></ProtectedPage>} />
      <Route path='/fleet/instances/:id' component={() => <ProtectedPage><InstanceDetail /></ProtectedPage>} />
      <Route path='/fleet/:id' component={() => <ProtectedPage><FleetInstanceDetails /></ProtectedPage>} />
      <Route path='/fleet/:id/crm' component={() => <ProtectedPage><InstanceCRM /></ProtectedPage>} />

      {/* Fleet Bulk Operations route */}
      <Route path='/fleet/bulk-operations' component={() => <ProtectedPage><FleetBulkOperations /></ProtectedPage>} />

      {/* Fleet Provisioning routes (Tier 3 - Managed On-Prem) */}
      <Route path='/fleet/provision' component={() => <ProtectedPage><ProvisioningList /></ProtectedPage>} />
      <Route path='/fleet/provision/new' component={() => <ProtectedPage><ProvisioningNew /></ProtectedPage>} />
      <Route path='/fleet/provision/:id' component={() => <ProtectedPage><ProvisioningDetail /></ProtectedPage>} />

      {/* Fleet Update Campaign routes (Remote Update Orchestration) */}
      <Route path='/fleet/updates' component={() => <ProtectedPage><UpdateCampaignList /></ProtectedPage>} />
      <Route path='/fleet/updates/new' component={() => <ProtectedPage><UpdateCampaignNew /></ProtectedPage>} />
      <Route path='/fleet/updates/:id' component={() => <ProtectedPage><UpdateCampaignDetail /></ProtectedPage>} />

      <Route path='/fleet/:instanceId/diagnostics' component={() => <ProtectedPage><DiagnosticList /></ProtectedPage>} />

      {/* Diagnostic Viewer routes */}
      <Route path='/diagnostics' component={() => <ProtectedPage><DiagnosticViewer /></ProtectedPage>} />
      <Route path='/diagnostics/:id' component={() => <ProtectedPage><DiagnosticViewer /></ProtectedPage>} />
      <Route path='/diagnostics/:instanceId/:diagnosticId' component={() => <ProtectedPage><DiagnosticViewer /></ProtectedPage>} />
      <Route path='/diagnostics/:instanceId/:diagnosticId/analysis' component={() => <ProtectedPage><DiagnosticAnalysis /></ProtectedPage>} />

      {/* Bundle Factory routes */}
      <Route path='/bundles' component={() => <ProtectedPage><BundleList /></ProtectedPage>} />
      <Route path='/bundles/new' component={() => <ProtectedPage><BundleFactory /></ProtectedPage>} />
      <Route path='/bundles/:id' component={() => <ProtectedPage><BundleDetails /></ProtectedPage>} />

      {/* Component versions management (admin) */}
      <Route path='/components' component={() => <ProtectedPage><ComponentVersions /></ProtectedPage>} />

      {/* Roles routes */}
      <Route path='/roles' component={() => <ProtectedPage><Roles /></ProtectedPage>} />
      <Route path='/roles/new' component={() => <ProtectedPage><RoleCreate /></ProtectedPage>} />
      <Route path='/roles/:id' component={() => <ProtectedPage><RoleDetails /></ProtectedPage>} />
      <Route path='/roles/:id/edit' component={() => <ProtectedPage><RoleEdit /></ProtectedPage>} />

      {/* Cache management routes */}
      <Route path='/cache-rules' component={() => <ProtectedPage><CacheRules /></ProtectedPage>} />
      <Route path='/cache' component={() => <ProtectedPage><CacheOverview /></ProtectedPage>} />

      {/* Analytics route */}
      <Route path='/analytics' component={() => <ProtectedPage><Analytics /></ProtectedPage>} />

      {/* User management route */}
      <Route path='/user-management' component={() => <ProtectedPage><UserManagement /></ProtectedPage>} />

      {/* Notifications inbox */}
      <Route path='/notifications' component={() => <ProtectedPage><NotificationInbox /></ProtectedPage>} />

      {/* Billing routes */}
      <Route path='/billing' component={() => <ProtectedPage><BillingOverview /></ProtectedPage>} />
      <Route path='/billing/history' component={() => <ProtectedPage><BillingHistory /></ProtectedPage>} />
      <Route path='/billing/plans' component={() => <ProtectedPage><PlanSelector /></ProtectedPage>} />
      <Route path='/billing/usage' component={() => <ProtectedPage><UsageDashboard /></ProtectedPage>} />

      {/* Settings routes */}
      <Route path='/settings' component={() => <ProtectedPage><Settings /></ProtectedPage>} />
      <Route path='/settings/mfa' component={() => <ProtectedPage><MfaSetup /></ProtectedPage>} />

      {/* Audit logs route */}
      <Route path='/audit' component={() => <ProtectedPage><AuditLogs /></ProtectedPage>} />

      {/* Health dashboard route */}
      <Route path='/health' component={() => <ProtectedPage><HealthDashboard /></ProtectedPage>} />

      {/* Observability dashboard route */}
      <Route path='/observability' component={() => <ProtectedPage><Observability /></ProtectedPage>} />

      {/* Sessions route */}
      <Route path='/sessions' component={() => <ProtectedPage><Sessions /></ProtectedPage>} />

      {/* Admin dashboard route */}
      <Route path='/admin/dashboard' component={() => <ProtectedPage><AdminDashboard /></ProtectedPage>} />

      {/* Reconciliation reports route (admin only) */}
      <Route path='/admin/reconciliation' component={() => <ProtectedPage><ReconciliationReports /></ProtectedPage>} />

      {/* Client shard stats route */}
      <Route path='/clients/:clientId/shards' component={() => <ProtectedPage><ShardStats /></ProtectedPage>} />

      {/* Client deployment archives route */}
      <Route path='/clients/:clientId/archives' component={() => <ProtectedPage><DeploymentArchives /></ProtectedPage>} />

      {/* Global credentials route */}
      <Route path='/credentials' component={() => <ProtectedPage><CredentialsGlobal /></ProtectedPage>} />

      {/* Local development tools routes */}
      <Route path='/local/localstack' component={() => <ProtectedPage><LocalStack /></ProtectedPage>} />
      <Route path='/local/azurite' component={() => <ProtectedPage><Azurite /></ProtectedPage>} />
      <Route path='/local/tunnels' component={() => <ProtectedPage><Tunnels /></ProtectedPage>} />
      <Route path='/local/secrets' component={() => <ProtectedPage><ProviderSecrets /></ProtectedPage>} />
      <Route path='/local/resources' component={() => <ProtectedPage><CloudResources /></ProtectedPage>} />
      <Route path='/local/devcontainer' component={() => <ProtectedPage><DevContainer /></ProtectedPage>} />

      {/* Stack overview route */}
      <Route path='/clients/:clientId/stacks/:stackId/overview' component={() => <ProtectedPage><StackOverview /></ProtectedPage>} />

      {/* Stack secrets route */}
      <Route path='/clients/:clientId/stacks/:stackId/secrets' component={() => <ProtectedPage><StackSecrets /></ProtectedPage>} />

        <Route path='*' component={() => <PageErrorBoundary><NotFound /></PageErrorBoundary>} />
        </Suspense>
      </Router>
    </ToastProvider>
    </BrandingProvider>
  );
};
export default App;
